import i18n from "../../../i18n";
import { Trans } from "react-i18next";import React, { useCallback, useEffect, useRef, useState } from "react";
import "./assess.css";
import { hrmsGet, resolveApiUrl } from "../../../api/api";
import { getAuthenticatedTenantId } from "../../../lib/employee";
import sessionDB from "../../../utils/sessionDB";
import {
  buildOnboardingAccessMessage,
  isOnboardingFeatureUnavailableError,
  isGenericOnboardingErrorMessage,
  handleOnboardingApiError,
  resolveOnboardingActionConfig } from
"../onboardingFeatureAccess";

const ASSET_ALLOCATION_FALLBACKS = {
  allocate: {
    title: "Allocate Asset",
    serviceCode: "HR08A",
    endpoint: "/saas/hrms/emp-asset-allocations/allocate",
    method: "POST"
  },
  update: {
    title: "Update Asset Allocation",
    serviceCode: "HR08U",
    endpoint: "/saas/hrms/emp-asset-allocations/update",
    method: "PUT"
  }
};
const ACCESS_CARD_FALLBACKS = {
  add: {
    title: "Add Access Card",
    serviceCode: "HR07A",
    endpoint: "/saas/hrms/access-card/add",
    method: "POST"
  },
  update: {
    title: "Update Access Card",
    serviceCode: "HR07U",
    endpoint: "/saas/hrms/access-card/update",
    method: "PUT"
  }
};

function formatAssetLabel(assetId) {
  const id = String(assetId || "").trim();
  return id ? `${i18n.t("common.asset")} ${id}` : "";
}

function getAssetActionDisplayLabel(action) {
  if (action === "update") {
    return `${i18n.t("common.update")} ${i18n.t("onboarding.assetAllocation")}`;
  }
  return i18n.t("onboarding.assetAllocation");
}

function getAccessActionDisplayLabel(action) {
  if (action === "update") {
    return `${i18n.t("common.update")} ${i18n.t("onboarding.accessCard")}`;
  }
  return `${i18n.t("common.add")} ${i18n.t("onboarding.accessCard")}`;
}

function formatDateForInput(dateStr) {
  if (dateStr == null || dateStr === "") return "";

  if (Array.isArray(dateStr) && dateStr.length >= 3) {
    const [year, month, day] = dateStr;
    const mm = String(month).padStart(2, "0");
    const dd = String(day).padStart(2, "0");
    return `${year}-${mm}-${dd}`;
  }

  if (dateStr instanceof Date && !Number.isNaN(dateStr.getTime())) {
    return dateStr.toISOString().slice(0, 10);
  }

  if (typeof dateStr === "number" && Number.isFinite(dateStr)) {
    const fromEpoch = new Date(dateStr);
    if (!Number.isNaN(fromEpoch.getTime())) return fromEpoch.toISOString().slice(0, 10);
  }

  let str = String(dateStr).trim();
  if (!str || str === "null" || str === "undefined") return "";

  if (str.includes("T")) str = str.split("T")[0];
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;

  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) {
    const [day, month, year] = str.split("-");
    return `${year}-${month}-${day}`;
  }

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(str)) {
    const [day, month, year] = str.split("/");
    return `${year}-${month}-${day}`;
  }

  if (/^\d{4}\/\d{2}\/\d{2}$/.test(str)) {
    const [year, month, day] = str.split("/");
    return `${year}-${month}-${day}`;
  }

  const parsed = new Date(str);
  if (!Number.isNaN(parsed.getTime()) && str.length >= 8) {
    return parsed.toISOString().slice(0, 10);
  }

  return "";
}

function firstNonEmpty(values) {
  return values.find((value) => value !== null && value !== undefined && String(value).trim() && String(value).trim() !== "null" && String(value).trim() !== "undefined");
}

function extractAssetList(payload, depth = 0) {
  if (payload == null || depth > 6) return [];
  if (Array.isArray(payload)) return payload;
  if (typeof payload !== "object") return [];

  const nestedKeys = ["content", "data", "result", "assets", "records", "items", "rows", "list", "orgAssets"];
  for (const key of nestedKeys) {
    const found = extractAssetList(payload[key], depth + 1);
    if (found.length) return found;
  }

  for (const value of Object.values(payload)) {
    if (Array.isArray(value) && value.length) return value;
  }
  return [];
}

// function normalizeAssetOptions(payload) {
//   const list = extractAssetList(payload);

//   return list
//   .map((item) => {
//     if (item == null) return null;
//     if (typeof item === "string" || typeof item === "number") {
//       const value = String(item).trim();
//       return value ? { id: value, label: value } : null;
//     }
//     if (typeof item !== "object") return null;

//     const code = firstNonEmpty([item.assetCode, item.asset_code, item.code, item.assetNo, item.asset_no]);
//     const name = firstNonEmpty([
//       item.assetName,
//       item.asset_name,
//       item.assetsName,
//       item.assets_name,
//       item.assetType,
//       item.asset_type,
//       item.assetTypeName,
//       item.asset_type_name,
//       item.typeName,
//       item.type_name,
//       item.type,
//       item.description,
//       item.name,
//       item.label,
//       item.title
//     ]);
//     const id = firstNonEmpty([
//       item.assetId,
//       item.assetsId,
//       item.asset_id,
//       item.assets_id,
//       item.orgAssetId,
//       item.org_asset_id,
//       item.id,
//       item.value,
//       code
//     ]);
//     const label = firstNonEmpty([
//       code && name ? `${code} - ${name}` : null,
//       name,
//       code,
//       id
//     ]);

//     if (!id || !label) return null;
//     return { id: String(id).trim(), label: String(label).trim() };
//   })
//   .filter(Boolean);
// }

function normalizeAssetOptions(payload) {
  const list = extractAssetList(payload);

  return list
    .map((item) => {
      if (!item || typeof item !== "object") return null;

      const id = item.assetId;

      if (id === null || id === undefined) return null;

      const label = [
        item.assetTag,
        item.deviceType,
        item.modelName
      ]
        .filter(Boolean)
        .join(" - ");

      return {
        id: String(id),
        label: label || formatAssetLabel(id)
      };
    })
    .filter(Boolean);
}

function extractAllocationRecords(payload, depth = 0) {
  if (payload == null || depth > 6) return [];
  if (Array.isArray(payload)) {
    return payload.filter((item) => item && typeof item === "object");
  }
  if (typeof payload !== "object") return [];

  const nestedKeys = ["content", "data", "result", "allocations", "records", "items", "rows", "list"];
  for (const key of nestedKeys) {
    const found = extractAllocationRecords(payload[key], depth + 1);
    if (found.length) return found;
  }

  for (const value of Object.values(payload)) {
    if (Array.isArray(value) && value.some((item) => item && typeof item === "object")) {
      return value.filter((item) => item && typeof item === "object");
    }
  }

  if (payload.assetId || payload.allocationId || payload.empId || payload.returnedOn || payload.returnedDate) {
    return [payload];
  }
  return [];
}

function idsMatch(left, right) {
  if (left == null || right == null) return false;
  const a = String(left).trim();
  const b = String(right).trim();
  return Boolean(a) && a === b;
}

function getRecordEmpId(record) {
  return String(
    firstNonEmpty([
      record?.empId,
      record?.employeeId,
      record?.emp_id,
      record?.employee_id,
      record?.employee?.empId,
      record?.employee?.employeeId,
      record?.employee?.id,
      record?.emp?.empId,
      record?.emp?.id
    ]) || ""
  ).trim();
}

function formatDateDdMmYyyy(isoDate) {
  const iso = formatDateForInput(isoDate);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return "";
  const [year, month, day] = iso.split("-");
  return `${day}-${month}-${year}`;
}

function toApiAssetCondition(value) {
  const raw = String(value || "").trim();
  const upper = raw.toUpperCase();
  if (["GOOD", "NEW", "REFURBISHED", "DAMAGED"].includes(upper)) return upper;
  if (/refurb/i.test(raw)) return "REFURBISHED";
  if (/damag/i.test(raw)) return "DAMAGED";
  if (/new/i.test(raw)) return "NEW";
  if (/good/i.test(raw)) return "GOOD";
  return "GOOD";
}

function getRecordAssetId(record) {
  return String(
    firstNonEmpty([
      record?.assetId,
      record?.orgAssetId,
      record?.assetsId,
      record?.asset_id,
      record?.org_asset_id,
      record?.assets_id,
      record?.asset?.assetId,
      record?.asset?.id,
      record?.assetID
    ]) || ""
  ).trim();
}

function getRecordReturnedOn(record) {
  return firstNonEmpty([
    record?.returnedOn,
    record?.returnedDate,
    record?.returnDate,
    record?.dateOfReturn,
    record?.returned_on,
    record?.returned_date,
    record?.return_date,
    record?.returnOn,
    record?.assetReturnedOn
  ]) || "";
}

function isAllocationReturned(record) {
  if (!record) return false;
  const status = String(record.status || record.allocationStatus || record.returnStatus || record.state || "").toLowerCase();
  if (status.includes("return")) return true;
  return Boolean(formatDateForInput(getRecordReturnedOn(record)));
}

function filterRecordsForEmployee(records, empId) {
  const list = records || [];
  const matchingEmp = list.filter((record) => idsMatch(getRecordEmpId(record), empId));
  if (matchingEmp.length) return matchingEmp;
  const hasOtherEmp = list.some((record) => getRecordEmpId(record) && !idsMatch(getRecordEmpId(record), empId));
  if (hasOtherEmp) return [];
  return list.filter((record) => !getRecordEmpId(record));
}

function getRecordAssetLabel(record, assetId) {
  return firstNonEmpty([
    record?.assetName,
    record?.asset_name,
    record?.assetTag,
    record?.asset_tag,
    record?.assetCode,
    record?.asset_code,
    record?.deviceType,
    record?.modelName,
    record?.asset?.assetName,
    record?.asset?.name,
    assetId ? formatAssetLabel(assetId) : null
  ]) || (assetId ? formatAssetLabel(assetId) : "");
}

function ensureAssetOption(options, assetId, label) {
  const id = String(assetId || "").trim();
  if (!id) return options || [];
  const list = Array.isArray(options) ? options : [];
  if (list.some((option) => idsMatch(option.id, id))) return list;
  return [...list, { id, label: label || formatAssetLabel(id) }];
}

function getRecordAllocationId(record) {
  if (!record || typeof record !== "object") return null;
  const dedicated = firstNonEmpty([
    record.allocationId,
    record.allocation_id,
    record.empAssetAllocationId,
    record.emp_asset_allocation_id,
    record.assetAllocationId,
    record.asset_allocation_id,
    record.allocId,
    record.alloc_id
  ]);
  if (dedicated) return String(dedicated).trim();

  const assetId = getRecordAssetId(record);
  if (record.id != null && String(record.id).trim() !== "") {
    if (!assetId || !idsMatch(record.id, assetId) || record.assetId != null || record.orgAssetId != null) {
      return String(record.id).trim();
    }
  }
  return null;
}

function collectAllocationIds(value, assetId, depth = 0, found = []) {
  if (value == null || depth > 6) return found;
  if (typeof value === "number" || typeof value === "string") {
    const str = String(value).trim();
    if (str && str !== "null" && !found.includes(str)) found.push(str);
    return found;
  }
  if (Array.isArray(value)) {
    value.forEach((item) => collectAllocationIds(item, assetId, depth + 1, found));
    return found;
  }
  if (typeof value !== "object") return found;

  const dedicated = getRecordAllocationId(value);
  if (dedicated && !found.includes(dedicated)) found.push(dedicated);

  Object.entries(value).forEach(([key, nested]) => {
    const lower = String(key).toLowerCase();
    if (lower.includes("allocation") && lower.endsWith("id")) {
      collectAllocationIds(nested, assetId, depth + 1, found);
    }
  });
  if (value.data) collectAllocationIds(value.data, assetId, depth + 1, found);
  if (value.content) collectAllocationIds(value.content, assetId, depth + 1, found);
  return found;
}

function pickAllocationIdFromPayload(payload, assetId) {
  const ids = collectAllocationIds(payload, assetId);
  const preferred = ids.find((id) => !idsMatch(id, assetId));
  return preferred || ids[0] || "";
}

function findEmployeeAllocation(records, empId, assetId, { includeReturned = true } = {}) {
  const list = filterRecordsForEmployee(records, empId).filter((record) => (
    includeReturned || !isAllocationReturned(record)
  ));
  const ranked = list.slice().sort((a, b) => {
    const idA = Number(getRecordAllocationId(a)) || 0;
    const idB = Number(getRecordAllocationId(b)) || 0;
    return idB - idA;
  });
  if (assetId) {
    const byAsset = ranked.find((record) => idsMatch(getRecordAssetId(record), assetId));
    if (byAsset) return byAsset;
  }
  return ranked.find((record) => getRecordAssetId(record)) || ranked[0] || null;
}

async function searchEmployeeAllocations(empId, headers) {
  const encoded = encodeURIComponent(empId);
  const getPaths = [
    `/saas/hrms/emp-asset-allocations/search?empId=${encoded}&page=0&size=500`,
    `/saas/hrms/emp-asset-allocations/search/employee/${encoded}`,
    `/saas/hrms/emp-asset-allocations/employee/${encoded}`,
    "/saas/hrms/emp-asset-allocations/search"
  ];

  for (const path of getPaths) {
    try {
      const response = await fetch(resolveApiUrl(path), {
        method: "GET",
        headers: {
          ...headers,
          Accept: "*/*",
          "x-service-code": "HR08S"
        }
      });
      if (!response.ok) continue;
      const body = await response.json().catch(() => ({}));
      console.log("[AssetAllocation] search", path, body);
      const records = extractAllocationRecords(body);
      const scoped = filterRecordsForEmployee(records, empId);
      if (scoped.length) return scoped;
      const employeeScopedPath = path.includes("empId=") || path.includes("/employee/");
      if (employeeScopedPath && records.length) return records;
    } catch (error) {
      console.warn("[AssetAllocation] search failed", path, error);
    }
  }

  try {
    const response = await fetch(resolveApiUrl("/saas/hrms/emp-asset-allocations/search"), {
      method: "POST",
      headers: {
        ...headers,
        Accept: "*/*",
        "Content-Type": "application/json",
        "x-service-code": "HR08S"
      },
      body: JSON.stringify({ empId: Number(empId) || empId })
    });
    if (response.ok) {
      const body = await response.json().catch(() => ({}));
      console.log("[AssetAllocation] search POST", body);
      const records = extractAllocationRecords(body);
      const scoped = filterRecordsForEmployee(records, empId);
      if (scoped.length) return scoped;
    }
  } catch (error) {
    console.warn("[AssetAllocation] search POST failed", error);
  }

  return [];
}

export default function Assess({ onBack, onNext, onSuccess, breadcrumb, isTabMode = false, isActive = true, saveTriggerRef, initialEmployeeId = "" }) {
  useEffect(() => {
    if (saveTriggerRef) {
      saveTriggerRef.current = {
        submit: handleSave
      };
    }
  });
  const [assetAction, setAssetAction] = useState("allocate");
  const [accessAction, setAccessAction] = useState("add");
  const [assetOptions, setAssetOptions] = useState([]);
  const [assetForm, setAssetForm] = useState({
    assetId: "",
    conditionOnAllocation: "Good",
    returnedDate: "",
    conditionOnReturn: "Good"
  });
  const [accessForm, setAccessForm] = useState({
    cardNumber: "",
    issueDate: "",
    accessLocation: "",
    issueReason: ""
  });
  const [accessErrors, setAccessErrors] = useState({});
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [banner, _setBanner] = useState(null);
  const setBanner = (val) => {
    _setBanner((prev) => {
      const next = typeof val === "function" ? val(prev) : val;
      if (next && next.type === "error") {
        if (isOnboardingFeatureUnavailableError(next.msg)) {
          return { type: "error", msg: i18n.t("onboarding.featureUnavailable") };
        }
        if (isGenericOnboardingErrorMessage(next.msg)) {
          return prev;
        }
      }
      return next;
    });
  };

  // Store backend IDs returned from asset/access APIs
  const [allocationId, setAllocationId] = useState(null); // allocationId used by /emp-asset-allocations/{allocationId}/return
  const [allocatedAssetId, setAllocatedAssetId] = useState("");
  const [cardId, setCardId] = useState(null); // cardId for access-card/update/{cardId}
  // pendingAssetId stores the raw assetId fetched from backend — used to
  // resolve the dropdown selection once assetOptions have been loaded.
  const pendingAssetId = useRef(null);
  const lastSavedRef = useRef(null);
  const assetsLoadedRef = useRef(false);

  const tenantObj = sessionDB.getTenantObj() || {};
  const tenantId = sessionDB.getTenantId() || tenantObj.tenantId || tenantObj.tenant_id || tenantObj.id || getAuthenticatedTenantId() || "";
  const tenantCode = sessionDB.getTenantCode() || tenantObj.tenantCode || tenantObj.tenant_code || "";
  const accessToken = sessionDB.getAccessToken() || "";
  const employeeId = String(initialEmployeeId || "").trim();

  const fetchAssetAndAccess = useCallback(async () => {
    if (!employeeId) return;
    try {
      const headers = {
        'x-tenant-code': tenantCode || sessionDB.getTenantCode() || "",
        'x-tenant-id': String(tenantId || sessionDB.getTenantId() || ""),
        'Content-Type': 'application/json',
        authorization: `Bearer ${accessToken || sessionDB.getAccessToken() || ""}`
      };

      const records = await searchEmployeeAllocations(employeeId, headers);
      const record =
        findEmployeeAllocation(records, employeeId, null, { includeReturned: false }) ||
        findEmployeeAllocation(records, employeeId);
      const draft = lastSavedRef.current?.empId === employeeId
        ? lastSavedRef.current
        : sessionDB.getSession()?.onboardingAssetAllocationDraft?.empId === employeeId
          ? sessionDB.getSession().onboardingAssetAllocationDraft
          : null;

      if (record) {
        const recordAllocationId = getRecordAllocationId(record);
        const rawAssetId = getRecordAssetId(record) || draft?.assetId || "";
        const formattedReturnDate = formatDateForInput(getRecordReturnedOn(record)) || formatDateForInput(draft?.returnedDate);
        setAllocationId(recordAllocationId || draft?.allocationId || null);
        setAllocatedAssetId(rawAssetId);
        pendingAssetId.current = rawAssetId || pendingAssetId.current;
        setAssetOptions((prev) => ensureAssetOption(prev, rawAssetId, getRecordAssetLabel(record, rawAssetId)));
        console.log("[AssetAllocation] loaded existing allocation", {
          allocationId: recordAllocationId,
          assetId: rawAssetId,
          returnedOn: formattedReturnDate,
          record
        });

        setAssetForm({
          assetId: String(rawAssetId || ""),
          conditionOnAllocation: record.conditionOnAllocation || record.condition || record.allocationCondition || "Good",
          returnedDate: formattedReturnDate || "",
          conditionOnReturn: record.conditionOnReturn || record.returnCondition || "Good"
        });
        if (rawAssetId || formattedReturnDate) {
          setAssetAction("update");
        }
      } else if (draft?.assetId || draft?.returnedDate) {
        pendingAssetId.current = draft.assetId || null;
        setAllocationId(draft.allocationId || null);
        setAllocatedAssetId(draft.assetId || "");
        setAssetOptions((prev) => ensureAssetOption(prev, draft.assetId, formatAssetLabel(draft.assetId)));
        setAssetForm((prev) => ({
          ...prev,
          assetId: String(draft.assetId || prev.assetId || ""),
          returnedDate: formatDateForInput(draft.returnedDate) || prev.returnedDate
        }));
        setAssetAction("update");
      } else {
        setAllocationId(null);
        setAllocatedAssetId("");
        setAssetAction("allocate");
        setAssetForm({
          assetId: "",
          conditionOnAllocation: "Good",
          returnedDate: "",
          conditionOnReturn: "Good"
        });
      }

      // CORRECT ENDPOINT: GET /saas/hrms/access-card/search/employee/{empId}
      // The old endpoint /search/{empId} returned cardId by ID, not by empId
      const accessRes = await fetch(
        resolveApiUrl(`/saas/hrms/access-card/search/employee/${Number(employeeId)}`),
        { method: 'GET', headers: { ...headers, 'x-service-code': 'HR07S' } }
      );

      if (accessRes.ok) {
        const body = await accessRes.json();
        // Response: ApiResponsePagedResponseAccessCardDTO → body.data.content[]
        const data = body.data || body;
        const candidates = [
        data?.content,
        data?.data?.content,
        data?.cards,
        data?.records,
        data?.items,
        data];

        const arrayCandidate = candidates.find((c) => Array.isArray(c));
        const record = arrayCandidate ? arrayCandidate[0] : data && typeof data === "object" && !Array.isArray(data) ? data : null;

        if (record && typeof record === "object") {
          // Extract cardId — required for update endpoint: PUT /access-card/update/{cardId}
          const recordCardId = record.cardId ?? record.id ?? record.card_id ?? null;
          setCardId(recordCardId);

          const cardNum = String(record.cardNo || record.cardNumber || record.cardNo || record.accessCardNo || "");
          const issueDt = record.cardIssueDate || record.issueDate || record.card_issue_date || "";
          const location = record.accessLocation || record.location || "";
          const reason = record.issueReason || record.reason || "";

          if (cardNum || issueDt || location) {
            setAccessForm({
              cardNumber: cardNum,
              issueDate: issueDt ? String(issueDt).split("T")[0] : "",
              accessLocation: location,
              issueReason: reason
            });
            setAccessAction("update");
          }
        }
      }
    } catch (err) {
      console.error("Failed to load asset and access card info", err);
    }
  }, [employeeId, tenantCode, tenantId, accessToken]);

  useEffect(() => {
    if (lastSavedRef.current?.empId && lastSavedRef.current.empId !== employeeId) {
      lastSavedRef.current = null;
    }
    setAllocationId(null);
    setAllocatedAssetId("");
    setAssetForm({
      assetId: "",
      conditionOnAllocation: "Good",
      returnedDate: "",
      conditionOnReturn: "Good"
    });
    setAssetAction("allocate");
  }, [employeeId]);

  useEffect(() => {
    if (!isActive) return;
    if (!employeeId) {
      setAssetForm({
        assetId: "",
        conditionOnAllocation: "Good",
        returnedDate: "",
        conditionOnReturn: "Good"
      });
      setAccessForm({
        cardNumber: "",
        issueDate: "",
        accessLocation: "",
        issueReason: ""
      });
      setAssetAction("allocate");
      setAccessAction("add");
      setAllocationId(null);
      setAllocatedAssetId("");
      setCardId(null);
      return;
    }
    fetchAssetAndAccess();
  }, [isActive, employeeId, fetchAssetAndAccess]);

  useEffect(() => {
    if (!isActive || assetsLoadedRef.current === true) return;

    const nextTenantId = String(
      sessionDB.getTenantId() ||
      sessionDB.getTenantObj()?.tenantId ||
      sessionDB.getTenantObj()?.tenant_id ||
      getAuthenticatedTenantId() ||
      tenantId ||
      ""
    ).trim();
    if (!nextTenantId) return;

    let cancelled = false;
    assetsLoadedRef.current = "loading";

    async function loadOrgAssets() {
      setAssetsLoading(true);
      try {
        const query = `/org/assets?tenantId=${encodeURIComponent(nextTenantId)}&page=0&size=500`;
        let data;
        try {
          data = await hrmsGet(query, "HR20A");
        } catch (primaryError) {
          console.warn("[AssetAllocation] org assets HR20A failed, retrying HR14A", primaryError);
          data = await hrmsGet(query, "HR14A");
        }
        if (cancelled) {
          assetsLoadedRef.current = false;
          return;
        }
        const options = ensureAssetOption(
          normalizeAssetOptions(data),
          pendingAssetId.current || lastSavedRef.current?.assetId
        );
        console.log("[AssetAllocation] org assets response", data);
        console.log("[AssetAllocation] org assets options", options);
        setAssetOptions(options);
        assetsLoadedRef.current = true;
      } catch (error) {
        if (cancelled) {
          assetsLoadedRef.current = false;
          return;
        }
        assetsLoadedRef.current = false;
        setAssetOptions([]);
        setBanner({ type: "error", msg: error.message || i18n.t("onboarding.errors.unableToLoadAssets") });
      } finally {
        if (!cancelled) setAssetsLoading(false);
      }
    }

    loadOrgAssets();
    return () => {
      cancelled = true;
      if (assetsLoadedRef.current === "loading") {
        assetsLoadedRef.current = false;
      }
    };
  }, [isActive, tenantId]);

  // When assetOptions load (after the initial fetch), resolve any pending asset ID
  useEffect(() => {
    if (!pendingAssetId.current) return;
    const rawId = String(pendingAssetId.current);
    const matchedOption = assetOptions.find(
      (o) => o.id === rawId || o.label.toLowerCase() === rawId.toLowerCase() || o.label.toLowerCase().includes(rawId.toLowerCase())
    );
    const resolvedId = matchedOption ? matchedOption.id : rawId;
    if (!matchedOption) {
      setAssetOptions((prev) => ensureAssetOption(prev, resolvedId, formatAssetLabel(resolvedId)));
    }
    setAssetForm((prev) => {
      if (prev.assetId === resolvedId) return prev;
      return { ...prev, assetId: resolvedId };
    });
  }, [assetOptions]);

  const assetActionConfig = resolveOnboardingActionConfig({
    submoduleNames: ["Asset Allocation", "Assets & Access"],
    action: assetAction,
    fallback: ASSET_ALLOCATION_FALLBACKS[assetAction] || ASSET_ALLOCATION_FALLBACKS.allocate
  });
  const accessActionConfig = resolveOnboardingActionConfig({
    submoduleNames: ["Access Card", "Assets & Access"],
    action: accessAction,
    fallback: ACCESS_CARD_FALLBACKS[accessAction] || ACCESS_CARD_FALLBACKS.add
  });

  function clearAccessFieldError(field) {
    setAccessErrors((prev) => {
      if (!prev[field]) return prev;
      return { ...prev, [field]: "" };
    });
  }

  function validateAccessCardFields() {
    const nextErrors = {};

    if (!accessForm.issueReason.trim()) {
      nextErrors.issueReason = i18n.t("onboarding.errors.issueReasonRequired");
    }

    if (accessForm.issueDate) {
      const today = new Date().toISOString().slice(0, 10);
      if (accessForm.issueDate > today) {
        nextErrors.issueDate = i18n.t("onboarding.errors.issueDateCannotBeFuture");
      }
    }

    setAccessErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }

  async function handleSave() {
    setBanner(null);

    if (!employeeId || !/^\d+$/.test(employeeId)) {
      setBanner({ type: "error", msg: i18n.t("onboarding.errors.validEmployeeIdForAssets") });
      return;
    }

    if (!assetActionConfig.isAvailable) {
      setBanner({
        type: "error",
        msg: buildOnboardingAccessMessage(
          i18n.t("onboarding.assetsAccess"),
          getAssetActionDisplayLabel(assetAction)
        )
      });
      return;
    }

    if (!accessActionConfig.isAvailable) {
      setBanner({
        type: "error",
        msg: buildOnboardingAccessMessage(
          i18n.t("onboarding.assetsAccess"),
          getAccessActionDisplayLabel(accessAction)
        )
      });
      return;
    }

    if (!assetForm.assetId) {
      setBanner({ type: "error", msg: i18n.t("onboarding.errors.selectAssetBeforeSave") });
      return;
    }

    if (!validateAccessCardFields()) {
      return;
    }

    if (!accessForm.cardNumber.trim() || !accessForm.issueDate || !accessForm.accessLocation.trim()) {
      setBanner({ type: "error", msg: i18n.t("onboarding.errors.completeAccessCardBeforeSave") });
      return;
    }

    const nextTenantId = String(tenantId || "").trim();
    const nextTenantCode = String(tenantCode || "").trim();
    const nextAccessToken = String(accessToken || "").trim();
    const commonHeaders = {
      "Content-Type": "application/json",
      Accept: "*/*",
      ...(nextTenantId ? { "X-Tenant-Id": nextTenantId } : {}),
      ...(nextTenantCode ? { "X-Tenant-Code": nextTenantCode } : {}),
      ...(nextAccessToken ? { Authorization: `Bearer ${nextAccessToken}` } : {})
    };

    const selectedAssetId = Number(assetForm.assetId) || assetForm.assetId;
    const selectedAssetIdStr = String(selectedAssetId);

    const assetEndpoint = "/saas/hrms/emp-asset-allocations/allocate";

    // CORRECT UPDATE ENDPOINT: PUT /saas/hrms/access-card/update/{cardId}
    // cardId is the record's primary key, NOT the empId
    const isAccessUpdate = accessAction === "update" && cardId != null;
    const accessEndpoint = isAccessUpdate ?
    `/saas/hrms/access-card/update/${cardId}` :
    accessActionConfig.endpoint || "/saas/hrms/access-card/add";

    const assetMethod = "POST";
    const accessMethod = isAccessUpdate ? "PUT" : accessActionConfig.method || "POST";

    const assetServiceCode = "HR08A";
    const accessServiceCode = isAccessUpdate ? "HR07U" : accessActionConfig.serviceCode || "HR07A";

    const assetPayload = {
      empId: Number(employeeId),
      assetId: Number(selectedAssetId) || selectedAssetId,
      conditionOnAllocation: assetForm.conditionOnAllocation || "Good"
    };

    const accessPayload = {
      cardNo: accessForm.cardNumber.trim(),
      cardNumber: accessForm.cardNumber.trim(),
      empId: Number(employeeId),
      cardIssueDate: accessForm.issueDate,
      issueDate: accessForm.issueDate,
      issueReason: accessForm.issueReason.trim(),
      accessLocation: accessForm.accessLocation.trim()
    };

    setSaving(true);

    try {
      console.log("[AssetAllocation] selected assetId", selectedAssetIdStr);
      console.log("[AssetAllocation] current allocationId", allocationId);
      console.log("[AssetAllocation] selected returned date", assetForm.returnedDate || null);

      const existingRecords = await searchEmployeeAllocations(employeeId, commonHeaders);
      const activeForThisEmployee = findEmployeeAllocation(existingRecords, employeeId, selectedAssetIdStr, { includeReturned: false });
      const anyForThisEmployee = findEmployeeAllocation(existingRecords, employeeId, selectedAssetIdStr);
      const savedForThisEmployee = lastSavedRef.current?.empId === employeeId
        ? lastSavedRef.current
        : sessionDB.getSession()?.onboardingAssetAllocationDraft?.empId === employeeId
          ? sessionDB.getSession().onboardingAssetAllocationDraft
          : null;
      const alreadyAllocatedToThisEmployee = Boolean(activeForThisEmployee);
      const alreadyCompletedForThisEmployee = Boolean(
        !activeForThisEmployee &&
        anyForThisEmployee &&
        isAllocationReturned(anyForThisEmployee)
      );
      let resolvedAllocationId = String(
        getRecordAllocationId(activeForThisEmployee) ||
        (alreadyCompletedForThisEmployee ? getRecordAllocationId(anyForThisEmployee) : "") ||
        (idsMatch(allocatedAssetId, selectedAssetIdStr) ? allocationId : "") ||
        (idsMatch(savedForThisEmployee?.assetId, selectedAssetIdStr) ? savedForThisEmployee?.allocationId : "") ||
        ""
      ).trim();

      if (alreadyAllocatedToThisEmployee || alreadyCompletedForThisEmployee) {
        const existingRecord = activeForThisEmployee || anyForThisEmployee;
        resolvedAllocationId = String(
          getRecordAllocationId(existingRecord) ||
          pickAllocationIdFromPayload(existingRecord, selectedAssetIdStr) ||
          resolvedAllocationId
        ).trim();
        setAllocationId(resolvedAllocationId || allocationId);
        setAllocatedAssetId(getRecordAssetId(existingRecord) || selectedAssetIdStr);
        console.log("[AssetAllocation] skip allocate — existing record for this employee", {
          assetId: selectedAssetIdStr,
          allocationId: resolvedAllocationId,
          alreadyReturned: alreadyCompletedForThisEmployee,
          record: existingRecord
        });
      } else {
        console.log("[AssetAllocation] allocate API call", { endpoint: assetEndpoint, method: assetMethod, payload: assetPayload });

        const assetResponse = await fetch(resolveApiUrl(assetEndpoint), {
          method: assetMethod,
          headers: {
            ...commonHeaders,
            "x-service-code": assetServiceCode
          },
          body: JSON.stringify(assetPayload)
        });

        const assetResponseBody = await assetResponse.json().catch(() => ({}));
        console.log("[AssetAllocation] allocate API response", assetResponseBody);

        if (!assetResponse.ok) {
          const errorMessage = String(assetResponseBody?.message || "");
          const isDuplicate =
            assetResponseBody?.errorCode === "DUPLICATE_ENTRY" ||
            /already allocated/i.test(errorMessage);

          if (isDuplicate) {
            const retryRecords = await searchEmployeeAllocations(employeeId, commonHeaders);
            const existing = findEmployeeAllocation(retryRecords, employeeId, selectedAssetIdStr, { includeReturned: false });
            if (existing) {
              resolvedAllocationId = String(getRecordAllocationId(existing) || "").trim();
              setAllocationId(resolvedAllocationId || null);
              setAllocatedAssetId(getRecordAssetId(existing) || selectedAssetIdStr);
              console.log("[AssetAllocation] duplicate treated as already saved for this employee", {
                assetId: selectedAssetIdStr,
                allocationId: resolvedAllocationId,
                record: existing
              });
            } else {
              throw new Error(assetResponseBody.message || i18n.t("onboarding.errors.assetAlreadyAllocated"));
            }
          } else {
            throw new Error(
              assetResponseBody.message ||
              i18n.t("onboarding.errors.assetAllocationFailed", { status: assetResponse.status })
            );
          }
        } else {
          resolvedAllocationId = pickAllocationIdFromPayload(assetResponseBody, selectedAssetIdStr) || resolvedAllocationId;
          if (!resolvedAllocationId) {
            const retryRecords = await searchEmployeeAllocations(employeeId, commonHeaders);
            const existing = findEmployeeAllocation(retryRecords, employeeId, selectedAssetIdStr, { includeReturned: false });
            resolvedAllocationId = String(
              getRecordAllocationId(existing) ||
              pickAllocationIdFromPayload(existing, selectedAssetIdStr) ||
              ""
            ).trim();
            console.log("[AssetAllocation] allocationId after allocate search", { resolvedAllocationId, record: existing });
          }
          setAllocationId(resolvedAllocationId || allocationId);
          setAllocatedAssetId(selectedAssetIdStr);
        }
      }

      const targetReturnRecord = [activeForThisEmployee, anyForThisEmployee].find((record) => (
        record && idsMatch(getRecordAllocationId(record), resolvedAllocationId)
      ));
      const targetAlreadyReturned = Boolean(targetReturnRecord && isAllocationReturned(targetReturnRecord));

      if (assetForm.returnedDate && (alreadyCompletedForThisEmployee || targetAlreadyReturned)) {
        console.log("[AssetAllocation] skip return — allocation already returned for this employee", {
          allocationId: resolvedAllocationId,
          record: targetReturnRecord || anyForThisEmployee
        });
      } else if (assetForm.returnedDate) {
        if (!resolvedAllocationId) {
          const retryRecords = await searchEmployeeAllocations(employeeId, commonHeaders);
          const existing = findEmployeeAllocation(retryRecords, employeeId, selectedAssetIdStr, { includeReturned: false });
          resolvedAllocationId = String(
            getRecordAllocationId(existing) ||
            pickAllocationIdFromPayload(existing, selectedAssetIdStr) ||
            pickAllocationIdFromPayload({ data: retryRecords }, selectedAssetIdStr) ||
            (savedForThisEmployee?.empId === employeeId ? savedForThisEmployee?.allocationId : "") ||
            ""
          ).trim();
          console.log("[AssetAllocation] return lookup", { resolvedAllocationId, record: existing, records: retryRecords });
        }
        if (!resolvedAllocationId) {
          throw new Error(i18n.t("onboarding.errors.allocationIdNotFound"));
        }

        const formattedReturnDate = formatDateForInput(assetForm.returnedDate);
        const formattedReturnDateUi = formatDateDdMmYyyy(formattedReturnDate);
        const conditionOnReturn = assetForm.conditionOnReturn || "Good";
        const conditionOnReturnEnum = toApiAssetCondition(conditionOnReturn);
        const returnEndpoint = `/saas/hrms/emp-asset-allocations/${resolvedAllocationId}/return`;
        const returnAttempts = [
          { payload: { returnedOn: formattedReturnDate, conditionOnReturn } },
          { payload: { returnedOn: formattedReturnDate, conditionOnReturn: conditionOnReturnEnum } },
          { payload: { returnedOn: formattedReturnDate } },
          { payload: { returnedOn: formattedReturnDateUi, conditionOnReturn } },
          { payload: { returnedOn: `${formattedReturnDate}T00:00:00`, conditionOnReturn } },
          { payload: { returnedDate: formattedReturnDate, conditionOnReturn } }
        ];

        let returnResponse = null;
        let returnResponseBody = {};
        let lastReturnError = null;

        for (const attempt of returnAttempts) {
          console.log("[AssetAllocation] return API call", { endpoint: returnEndpoint, method: "POST", payload: attempt.payload });
          returnResponse = await fetch(resolveApiUrl(returnEndpoint), {
            method: "POST",
            headers: {
              ...commonHeaders,
              "x-service-code": "HR08U"
            },
            body: JSON.stringify(attempt.payload)
          });
          returnResponseBody = await returnResponse.json().catch(() => ({}));
          console.log("[AssetAllocation] return API response", returnResponseBody);
          if (returnResponse.ok) {
            lastReturnError = null;
            break;
          }
          lastReturnError = returnResponseBody;
          const errorCode = String(returnResponseBody?.errorCode || "");
          const errorMessage = String(returnResponseBody?.message || "");
          if (/already been returned/i.test(errorMessage)) {
            if (alreadyCompletedForThisEmployee || targetAlreadyReturned) {
              console.log("[AssetAllocation] skip further return retries — allocation already returned");
              lastReturnError = null;
            }
            break;
          }
          if (!errorCode.includes("INVALID_REQUEST_BODY") && !errorMessage.toLowerCase().includes("invalid request body")) {
            break;
          }
        }

        if (lastReturnError && !returnResponse?.ok) {
          throw new Error(
            lastReturnError?.message ||
            i18n.t("onboarding.errors.assetReturnFailed", { status: returnResponse?.status })
          );
        }
      }

      pendingAssetId.current = selectedAssetIdStr;
      const savedSnapshot = {
        empId: employeeId,
        assetId: selectedAssetIdStr,
        returnedDate: formatDateForInput(assetForm.returnedDate) || "",
        allocationId: resolvedAllocationId || allocationId || null
      };
      lastSavedRef.current = savedSnapshot;
      sessionDB.updateSession({ onboardingAssetAllocationDraft: savedSnapshot });
      setAllocatedAssetId(selectedAssetIdStr);
      setAssetOptions((prev) => ensureAssetOption(prev, selectedAssetIdStr, formatAssetLabel(selectedAssetIdStr)));
      setAssetForm((prev) => ({
        ...prev,
        assetId: selectedAssetIdStr,
        returnedDate: savedSnapshot.returnedDate || prev.returnedDate
      }));

      console.log("[AccessCard] save API call", { endpoint: accessEndpoint, method: accessMethod, payload: accessPayload });

      const accessResponse = await fetch(resolveApiUrl(accessEndpoint), {
        method: accessMethod,
        headers: {
          ...commonHeaders,
          "x-service-code": accessServiceCode
        },
        body: JSON.stringify(accessPayload)
      });

      if (!accessResponse.ok) {
        const accessError = await accessResponse.json().catch(() => ({}));
        throw new Error(
          accessError.message ||
          i18n.t("onboarding.errors.accessCardSaveFailed", { status: accessResponse.status })
        );
      }
      const accessResponseBody = await accessResponse.json().catch(() => ({}));
      console.log("[AccessCard] save API response", accessResponseBody);

      setBanner({ type: "success", msg: i18n.t("onboarding.messages.assetsAccessSavedSuccessfully") });
      await fetchAssetAndAccess();
      setAssetForm((prev) => ({
        ...prev,
        assetId: selectedAssetIdStr || prev.assetId,
        returnedDate: savedSnapshot.returnedDate || prev.returnedDate
      }));
      setAssetOptions((prev) => ensureAssetOption(prev, selectedAssetIdStr, formatAssetLabel(selectedAssetIdStr)));
      if (onSuccess) onSuccess();
    } catch (error) {
      handleOnboardingApiError(null, error.message, { setBanner });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={`aas-page${isTabMode ? " is-tab-mode" : ""}`}>
      <main className="aas-main">
        {!isTabMode && breadcrumb &&
        <div className="aas-breadcrumb" aria-label={i18n.t("common.breadcrumb")}>
            {breadcrumb.map((item, index) =>
          <React.Fragment key={item.label}>
                {index > 0 && <span className="aas-bc-sep">/</span>}
                <button
              className={`aas-bc-item${item.active ? " active" : ""}`}
              onClick={item.active ? undefined : item.onClick}
              disabled={item.active}>
              
                  {item.label}
                </button>
              </React.Fragment>
          )}
          </div>
        }

        {!isTabMode &&
        <div className="aas-page-header">
            <div className="aas-page-header-row">
              <div>
                <h1 className="aas-page-title"><Trans i18nKey="onboarding.assetsAccess" /></h1>
                <p className="aas-page-sub"><Trans i18nKey="onboarding.manageEmployeeAssetAllocationAndAccessCardDetails" /></p>
              </div>
              <button
              className="aas-header-action"
              type="button"
              onClick={() => {
                const nextIsUpdate = assetAction !== "update" || accessAction !== "update";
                setAssetAction(nextIsUpdate ? "update" : "allocate");
                setAccessAction(nextIsUpdate ? "update" : "add");
              }}>
              
                {assetAction === "update" && accessAction === "update"
                  ? i18n.t("onboarding.addNew")
                  : i18n.t("common.update")}
              </button>
            </div>
          </div>
        }

        <div className="aas-grid">
          <section className="aas-card">
            <div className="aas-card-head">
              <h2 className="aas-card-title"><Trans i18nKey="onboarding.assetAllocation" /></h2>
              <p className="aas-card-sub"><Trans i18nKey="dateTime.allocateOrUpdateEmployeeAssets" /></p>
            </div>
            <div className="aas-form-grid">
              <div className="aas-field">
                <label className="aas-label required"><Trans i18nKey="common.asset" /></label>
                <select
                  className="aas-select"
                  value={assetForm.assetId}
                  onChange={(event) => setAssetForm((prev) => ({ ...prev, assetId: event.target.value }))}
                  disabled={assetsLoading && !assetForm.assetId}>
                  
                  <option value="">
                    {assetsLoading
                      ? i18n.t("onboarding.loadingAssets")
                      : assetOptions.length || assetForm.assetId
                        ? i18n.t("onboarding.selectAsset")
                        : i18n.t("onboarding.noAssetsAvailable")}
                  </option>
                  {ensureAssetOption(assetOptions, assetForm.assetId).map((option) =>
                  <option key={option.id} value={option.id}>{option.label}</option>
                  )}
                </select>
              </div>
              <div className="aas-field">
                <label className="aas-label required"><Trans i18nKey="onboarding.conditionOnAllocation" /></label>
                <select className="aas-select" value={assetForm.conditionOnAllocation} onChange={(event) => setAssetForm((prev) => ({ ...prev, conditionOnAllocation: event.target.value }))}>
                  <option value="Good"><Trans i18nKey="onboarding.good" /></option>
                  <option value="New"><Trans i18nKey="status.new" /></option>
                  <option value="Refurbished"><Trans i18nKey="onboarding.refurbished" /></option>
                </select>
              </div>
              <div className="aas-field">
                <label className="aas-label"><Trans i18nKey="dateTime.returnedDate" /></label>
                <input className="aas-input" type="date" value={assetForm.returnedDate} onChange={(event) => setAssetForm((prev) => ({ ...prev, returnedDate: event.target.value }))} />
              </div>
              <div className="aas-field">
                <label className="aas-label required"><Trans i18nKey="onboarding.conditionOnReturn" /></label>
                <select className="aas-select" value={assetForm.conditionOnReturn} onChange={(event) => setAssetForm((prev) => ({ ...prev, conditionOnReturn: event.target.value }))}>
                  <option value="Good"><Trans i18nKey="onboarding.good" /></option>
                  <option value="Damaged"><Trans i18nKey="onboarding.damaged" /></option>
                </select>
              </div>
            </div>
          </section>

          <section className="aas-card">
            <div className="aas-card-head">
              <h2 className="aas-card-title"><Trans i18nKey="onboarding.accessCard" /></h2>
              <p className="aas-card-sub"><Trans i18nKey="dateTime.addOrUpdateAccessCards" /></p>
            </div>
            <div className="aas-form-grid">
              <div className="aas-field">
                <label className="aas-label required"><Trans i18nKey="onboarding.cardNumber" /></label>
                <input className="aas-input" placeholder={i18n.t("onboarding.acXxxxxxx")} value={accessForm.cardNumber} onChange={(event) => setAccessForm((prev) => ({ ...prev, cardNumber: event.target.value }))} />
              </div>
              <div className="aas-field">
                <label className="aas-label required"><Trans i18nKey="dateTime.issueDate" /></label>
                <input
                  className={`aas-input${accessErrors.issueDate ? " error" : ""}`}
                  type="date"
                  value={accessForm.issueDate}
                  onChange={(event) => {
                    const value = event.target.value;
                    setAccessForm((prev) => ({ ...prev, issueDate: value }));
                    clearAccessFieldError("issueDate");
                  }}
                  onBlur={(event) => {
                    const value = event.target.value;
                    if (!value) return;
                    const today = new Date().toISOString().slice(0, 10);
                    if (value > today) {
                      setAccessErrors((prev) => ({
                        ...prev,
                        issueDate: i18n.t("onboarding.errors.issueDateCannotBeFuture")
                      }));
                    }
                  }} />
                <p className="aas-error" role="alert">{accessErrors.issueDate || ""}</p>
              </div>
              <div className="aas-field">
                <label className="aas-label required"><Trans i18nKey="onboarding.accessLocation" /></label>
                <select className="aas-select" value={accessForm.accessLocation} onChange={(event) => setAccessForm((prev) => ({ ...prev, accessLocation: event.target.value }))}>
                  <option value=""><Trans i18nKey="common.selectLocation" /></option>
                  <option value="Head Office"><Trans i18nKey="onboarding.headOffice" /></option>
                  <option value="Branch Office"><Trans i18nKey="onboarding.branchOffice" /></option>
                  <option value="Warehouse"><Trans i18nKey="inventory.warehouse" /></option>
                  <option value="Factory Floor"><Trans i18nKey="onboarding.factoryFloor" /></option>
                  <option value="Server Room"><Trans i18nKey="onboarding.serverRoom" /></option>
                </select>
              </div>
              <div className="aas-field span2">
                <label className="aas-label required"><Trans i18nKey="onboarding.issueReason" /></label>
                <textarea
                  className={`aas-textarea${accessErrors.issueReason ? " error" : ""}`}
                  rows="4"
                  placeholder={i18n.t("onboarding.reasonForIssuingAccessCard")}
                  value={accessForm.issueReason}
                  onChange={(event) => {
                    setAccessForm((prev) => ({ ...prev, issueReason: event.target.value }));
                    clearAccessFieldError("issueReason");
                  }}
                  onBlur={(event) => {
                    if (!event.target.value.trim()) {
                      setAccessErrors((prev) => ({
                        ...prev,
                        issueReason: i18n.t("onboarding.errors.issueReasonRequired")
                      }));
                    }
                  }} />
                <p className="aas-error" role="alert">{accessErrors.issueReason || ""}</p>
              </div>
            </div>
          </section>
        </div>

        {(onBack || onNext || banner) &&
        <div className="aas-actions">
            {banner &&
          <div className={`aas-banner ${banner.type || "error"}`} role="alert">
                {banner.msg}
              </div>
          }
            {!isTabMode &&
          <div className="aas-actions-row" style={{ display: "flex", width: "100%", justifyContent: "space-between", alignItems: "center", gap: "10px" }}>
                {onBack ? <button className="aas-secondary-btn" type="button" onClick={onBack}><Trans i18nKey="formActions.back" /></button> : <span />}
                <button className="aas-primary-btn" type="button" onClick={handleSave} disabled={saving}>
                  {saving ? i18n.t("localConveyance.saving") : i18n.t("formActions.save")}
                </button>
                {onNext ? <button className="aas-secondary-btn" type="button" onClick={onNext}><Trans i18nKey="pagination.next2" /></button> : null}
              </div>
          }
          </div>
        }
      </main>
    </div>);

}
